import React, { useState, useRef, useEffect } from 'react';
import client from '../api/client';

const MAX_MESSAGE_LENGTH = 1000;
const MAX_FILES = 5;

export function SupportTicketModal({ open, onClose, t, dir }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setSubject('');
      setMessage('');
      setPriority('medium');
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const sub = subject.trim();
    const msg = message.trim();
    if (!sub || !msg || submitting) return;
    setError(null);
    setSubmitting(true);

    try {
      const files = fileInputRef.current?.files ? Array.from(fileInputRef.current.files).slice(0, MAX_FILES) : [];
      if (files.length > 0) {
        const form = new FormData();
        form.append('subject', sub);
        form.append('message', msg);
        form.append('priority', priority);
        files.forEach((f) => form.append('files', f));
        await client.post('/api/support/tickets', form, { timeout: 60000 });
      } else {
        await client.post('/api/support/tickets', { subject: sub, message: msg, priority }, { timeout: 60000 });
      }
      setSuccess(true);
      setTimeout(() => {
        onClose?.();
      }, 1500);
    } catch (err) {
      const msgErr = err.response?.data?.error ?? err.message ?? 'Request failed';
      setError(typeof msgErr === 'string' ? msgErr : JSON.stringify(msgErr));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="console-modal-backdrop"
      onClick={() => !submitting && onClose?.()}
      role="presentation"
    >
      <div
        className="console-support-ticket-modal ui-modal ui-modal--md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-ticket-modal-title"
        onClick={(e) => e.stopPropagation()}
        dir={dir}
      >
        <div className="console-support-ticket-modal-header ui-modal__header">
          <h2 id="support-ticket-modal-title" className="console-support-ticket-modal-title">
            {t('supportTicketTitle')}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="console-support-ticket-modal-body ui-modal__body">
          {success && (
            <div className="console-support-ticket-success" role="status">
              {t('supportTicketCreated')}
            </div>
          )}
          {error && (
            <div className="console-support-ticket-error" role="alert">
              {error}
            </div>
          )}
          <div className="console-support-ticket-field">
            <label className="console-support-ticket-label">{t('supportTicketSubject')}</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('placeholderTicketSubject')}
              className="console-support-ticket-input"
              maxLength={500}
              disabled={submitting || success}
            />
          </div>
          <div className="console-support-ticket-field">
            <label className="console-support-ticket-label">{t('supportTicketPriority')}</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="console-support-ticket-input"
              disabled={submitting || success}
            >
              <option value="low">{t('supportTicketPriorityLow')}</option>
              <option value="medium">{t('supportTicketPriorityMedium')}</option>
              <option value="high">{t('supportTicketPriorityHigh')}</option>
            </select>
          </div>
          <div className="console-support-ticket-field">
            <div className="console-support-ticket-label-row">
              <label className="console-support-ticket-label">{t('supportTicketMessage')}</label>
              <span className="console-support-ticket-char-count">
                {message.length} / {MAX_MESSAGE_LENGTH}
              </span>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
              placeholder={t('placeholderTicketMessage')}
              rows={5}
              maxLength={MAX_MESSAGE_LENGTH}
              className="console-support-ticket-input console-support-ticket-textarea"
              disabled={submitting || success}
            />
          </div>
          <div className="console-support-ticket-field">
            <label className="console-support-ticket-label">{t('supportTicketAttachmentsOptional')}</label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif,.webp,.zip"
              className="console-support-ticket-input console-support-ticket-file"
              disabled={submitting || success}
            />
          </div>
        </form>
        <div className="console-support-ticket-modal-footer ui-modal__footer">
          <button
            type="button"
            className="console-support-ticket-btn console-support-ticket-btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            className="console-support-ticket-btn console-support-ticket-btn-submit"
            onClick={handleSubmit}
            disabled={!subject.trim() || !message.trim() || submitting || success}
          >
            {submitting ? t('supportTicketSubmitting') : t('supportTicketSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SupportTicketModal;
